// Shared HTML-paste sanitization for Summernote editors (Word/Outlook cleanup, font validation).

export const validateUrls = (obj: JQuery): void =>
{
  obj.find('a').each((_, link) =>
  {
    $(link).attr('target', '_blank');
  });
};

export const CleanPastedHTML = (input: string): string =>
{
  let output = input;

  // Remove Mso classes
  const stringStripper = /(class=(")?Mso[a-zA-Z]+(")?)/g;
  output = output.replace(stringStripper, '');

  // Strip comments
  const commentSripper = /<!--(.*?)-->/g;
  output = output.replace(commentSripper, '');

  // Remove meta tags
  const tagStripper = /<(\/)?( meta|link|html|title|head|body|\?xml:|st1:|o:)(.*?)>/gi;
  output = output.replace(tagStripper, '');

  // Remove p inside li/ul
  output = output.replace(/(<li[^>]*>\s*)<p\b[^>]*>([\s\S]*?)<\/p>(\s*<\/li>)/g, '$1$2$3');

  // Remove bad tags
  const badTags = ['script', 'applet', 'embed', 'noframes', 'noscript'];
  badTags.forEach(tag =>
  {
    const badTagStripper = new RegExp(`<${tag}.*?${tag}(.*?)>`, 'g');
    output = output.replace(badTagStripper, '');
  });

  // Remove bad attributes
  const badAttributes = ['start'];
  badAttributes.forEach(attr =>
  {
    const attributeStripper = new RegExp(` ${attr}="(.*?)"`, 'gi');
    output = output.replace(attributeStripper, '');
  });

  return output.replaceAll('\t', '');
};

export const validateFontFamily = (cssString: string): string =>
{
  const allowedFonts = [
    'Arial',
    'Arial Black',
    'Comic Sans Ms',
    'Roboto',
    'Courier New',
    'Helvetica',
  ];
  const fontFamilyRegex = /font-family:\s*["']?([^;"]+)["']?[,;]?/i;
  const matchFontFamily = cssString.match(fontFamilyRegex);

  if (matchFontFamily)
  {
    const currentFont = matchFontFamily[1]!.trim();

    if (!allowedFonts.includes(currentFont))
    {
      const newFontFamily = `font-family: "${allowedFonts[0]}"`;
      cssString = cssString.replace(fontFamilyRegex, newFontFamily);
    }
  }

  return cssString;
};

export const validateFontFace = (htmlString: string): string =>
{
  const allowedFonts = [
    'Roboto',
    'Arial',
    'Arial Black',
    'Comic Sans Ms',
    'Courier New',
    'Helvetica',
  ];
  const fontFaceRegex = /<font[^>]*\bface="([^"]*)"[^>]*>/gi;

  return htmlString.replace(fontFaceRegex, (match, fontFace) =>
  {
    const fonts = fontFace.split(',').map((f: string) => f.trim());
    const validFont = fonts.find((font: string) => allowedFonts.includes(font));
    const newFont = validFont ?? allowedFonts[0];
    return match.replace(fontFace, newFont);
  });
};

// Word often encodes bullet/decorative glyphs as ordinary-looking characters
// (e.g. the letter "l") styled with a symbol-encoding font like Wingdings -
// in that font the codepoint renders as a bullet/checkmark, but it's a
// Private-Use-Area codepoint with no valid glyph in any normal font.
// validateFontFamily/validateFontFace correctly swap the font name to an
// allowed one, but that leaves the underlying codepoint untouched - the new
// font has nothing to render for it, so the browser shows a "tofu" box (a
// black/hollow rectangle) instead. Removing these elements entirely (rather
// than re-fonting them) avoids the artifact, since the content is
// meaningless without the specific symbol font anyway.
const SYMBOL_FONTS = ['wingdings', 'wingdings 2', 'wingdings 3', 'webdings', 'symbol'];

const isSymbolFont = (fontValue: string | null | undefined): boolean =>
{
  if (!fontValue) return false;
  const lower = fontValue.toLowerCase();
  return SYMBOL_FONTS.some(font => lower.includes(font));
};

export const stripSymbolFontGlyphs = (container: HTMLElement): void =>
{
  // <font face="Wingdings">...</font>
  container.querySelectorAll('font[face]').forEach(el =>
  {
    if (isSymbolFont(el.getAttribute('face')))
    {
      el.remove();
    }
  });

  // Inline style="font-family: Wingdings" (or within a longer style string)
  container.querySelectorAll<HTMLElement>('[style*="font-family"]').forEach(el =>
  {
    if (isSymbolFont(el.style.fontFamily))
    {
      el.remove();
    }
  });
};

export const stripWordOnlineEopMarkers = (container: HTMLElement): void =>
{
  // Primary signal: Word own class name for this marker. Must run
  // before any generic class-stripping elsewhere, which would remove this
  // name without removing the element or its background-color style.
  container.querySelectorAll('[class*="EOP"]').forEach(el => el.remove());

  container.querySelectorAll<HTMLElement>('[style*="background-color"]').forEach(el =>
  {
    const textContent = (el.textContent || '').replace(/\u00a0/g, ' ').trim();
    const bg = el.style.backgroundColor;
    if (textContent === '' && bg && bg !== 'transparent' && bg !== 'inherit')
    {
      el.remove();
    }
  });
};

// Ensures a live selection exists inside editableEl before a raw range insert
export const ensureEditableSelection = (editableEl: HTMLElement): void =>
{
  editableEl.focus();

  const selection = window.getSelection();
  const hasSelectionInside =
    !!selection && selection.rangeCount > 0 && editableEl.contains(selection.getRangeAt(0).startContainer);

  if (hasSelectionInside) return;

  const range = document.createRange();
  range.selectNodeContents(editableEl);
  range.collapse(false);
  selection?.removeAllRanges();
  selection?.addRange(range);
};

// Block tags whose start tag makes the HTML parser close an open <p>.
const PARAGRAPH_CLOSING_BLOCKS = [
  'address', 'article', 'aside', 'blockquote', 'dd', 'details', 'dl', 'dt', 'div',
  'fieldset', 'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5',
  'h6', 'header', 'hr', 'li', 'main', 'nav', 'ol', 'p', 'pre', 'section', 'table', 'ul',
];
const BLOCK_IN_PARAGRAPH_SELECTOR = PARAGRAPH_CLOSING_BLOCKS.map(tag => `p ${tag}`).join(', ');

const isBlockElement = (node: Node | null): boolean =>
  !!node && node.nodeType === Node.ELEMENT_NODE &&
  PARAGRAPH_CLOSING_BLOCKS.includes((node as Element).tagName.toLowerCase());

// A split-off piece with nothing visible left in it (only <br>, empty
// wrappers or cursor-anchor characters).
const isVisuallyEmpty = (el: Element): boolean =>
  (el.textContent ?? '').replaceAll(String.fromCharCode(0xFEFF), '').trim().length === 0 &&
  !el.querySelector('img, input, iframe, video, audio, embed, object, canvas, svg, hr, table');

// Moves `block` out of its <p> ancestor, splitting every element in between
// so the content before and after it stays where it was.
const hoistBlockOutOfParagraph = (block: Element, paragraph: Element): void =>
{
  let parent = block.parentElement;

  while (parent)
  {
    const tail = parent.cloneNode(false) as Element;
    while (block.nextSibling)
    {
      tail.appendChild(block.nextSibling);
    }

    parent.after(block);
    if (tail.hasChildNodes()) block.after(tail);
    if (isVisuallyEmpty(tail)) tail.remove();
    if (isVisuallyEmpty(parent)) parent.remove();

    if (parent === paragraph) break;
    parent = block.parentElement;
  }
};

// The editor DOM is built through the DOM API, so it accepts a block nested in
// a <p> (e.g. a paste inserted while the caret sat inside a styled line). Once
// saved, the HTML parser closes that <p> and re-opens its formatting elements
// (<b>, <font>, <u>...) with their styles inside every following block, so the
// posted reply picks up formatting the editor never showed. Hoisting those
// blocks out keeps the editor DOM identical to what the parser will produce.
export const hoistBlocksOutOfParagraphs = (root: HTMLElement): void =>
{
  // Each pass fixes one block; bounded in case of pathological markup
  for (let guard = 0; guard < 500; guard++)
  {
    const block = root.querySelector(BLOCK_IN_PARAGRAPH_SELECTOR);
    if (!block) return;

    const paragraph = block.parentElement?.closest('p');
    if (!paragraph || !root.contains(paragraph)) return;

    hoistBlockOutOfParagraph(block, paragraph);
  }
};

// Resolves any CSS colour syntax (named, hex, rgb, hsl) to rgba components by
// letting the browser normalize it.
const resolveColor = (value: string): [number, number, number, number] | null =>
{
  if (!value) return null;

  const probe = document.createElement('span');
  probe.style.display = 'none';
  probe.style.color = '';
  probe.style.color = value;

  // An unparseable value leaves color empty
  if (!probe.style.color) return null;

  document.body.appendChild(probe);
  const computed = window.getComputedStyle(probe).color;
  probe.remove();

  const match = computed.match(/rgba?\(([^)]+)\)/);
  if (!match) return null;

  const parts = match[1]!.split(',').map(p => parseFloat(p.trim()));
  const [r, g, b] = parts;
  if (r === undefined || g === undefined || b === undefined) return null;

  return [r, g, b, parts[3] ?? 1];
};

// Pasted markup (Word, Outlook, Excel) commonly sets an explicit background but
// no `color`, relying on the source document's default black text. Left alone,
// that text inherits the app's colour - which is light in dark mode, giving
// white-on-white. Pin an explicit colour so the paste renders the same in both
// themes, picked from the background's luminance so dark backgrounds still work.
export const preserveContrastOnBackgrounds = (root: HTMLElement): void =>
{
  root.querySelectorAll<HTMLElement>('[style]').forEach(element =>
  {
    // Already carries its own colour: nothing to preserve
    if (element.style.color) return;

    const background = element.style.backgroundColor || element.style.background;
    const rgba = resolveColor(background);
    if (!rgba) return;

    const [r, g, b, alpha] = rgba;

    // Transparent backgrounds inherit from the page, which is what we want
    if (alpha < 0.5) return;

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    element.style.color = luminance > 140 ? '#000000' : '#ffffff';
  });
};

// Pasted lists (Word, Google Docs, web pages) pin their marker through inline
// list-style / list-style-type. That inline style survives the toolbar's
// bullet <-> numbered conversion and keeps forcing the old marker, so the list
// looks unchanged. Marker styling is left to the editor CSS instead.
export const stripListMarkerStyles = (root: HTMLElement): void =>
{
  root.querySelectorAll<HTMLElement>('ul, ol, li').forEach(el =>
  {
    el.style.removeProperty('list-style');
    el.style.removeProperty('list-style-type');
    el.style.removeProperty('list-style-image');

    if (!el.getAttribute('style')?.trim())
    {
      el.removeAttribute('style');
    }
  });

  // Legacy type="disc|circle|square|1|a" attribute pins the marker too
  root.querySelectorAll('ul[type], ol[type], li[type]').forEach(el => el.removeAttribute('type'));
};

// Cleans `htmlString`, extracts any <style> rules into inline styles, validates
// fonts, and inserts the result at the current selection.
export const changeStyles = (htmlString: string): void =>
{
  // Only a parsing container: its children are inserted, not the container itself
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = CleanPastedHTML(htmlString);

  // Google Docs wraps the whole copy in <b style="font-weight:normal">; it only
  // carries a GUID, and its <b> would be re-applied to every block on save.
  tempDiv.querySelectorAll('b[id^="docs-internal-guid"]').forEach(wrapper =>
  {
    wrapper.replaceWith(...Array.from(wrapper.childNodes));
  });

  stripSymbolFontGlyphs(tempDiv);
  stripWordOnlineEopMarkers(tempDiv);

  const styleElement = tempDiv.querySelector('style');
  if (styleElement)
  {
    const styles = styleElement.innerHTML;
    styleElement.remove();

    const cssRules: Record<string, string> = {};
    styles.split('}').forEach(rule =>
    {
      const parts = rule.split('{');
      if (parts.length === 2)
      {
        const selector = parts[0]!.trim();
        const declarations = parts[1]!
          .trim()
          .split(';')
          .map(d => d.trim())
          .filter(d => d);
        cssRules[selector] = declarations.join('; ');
      }
    });

    Object.keys(cssRules).forEach(selector =>
    {
      const elements = tempDiv.querySelectorAll(selector);
      elements.forEach(element =>
      {
        const currentStyle = element.getAttribute('style') || '';
        const newStyle = validateFontFamily(
          `${currentStyle !== '' ? currentStyle + '; ' : ''}${cssRules[selector]}`,
        );
        element.setAttribute('style', newStyle);
      });
    });
  }

  // Classes/ids are only useful to match the <style> rules above (e.g. Excel
  // Online's class-based cell styles) - strip them regardless of whether a
  // <style> block was present, so pasted markup never inherits the app's own CSS.
  tempDiv.querySelectorAll('[class]').forEach(el => el.removeAttribute('class'));
  tempDiv.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));

  // Must run after the <style> rules above are inlined, so backgrounds that
  // came from class-based rules are seen too
  preserveContrastOnBackgrounds(tempDiv);
  stripListMarkerStyles(tempDiv);

  tempDiv.innerHTML = validateFontFace(tempDiv.innerHTML);

  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0)
  {
    const range = selection.getRangeAt(0);

    // Insert the pasted nodes themselves: a wrapper would keep the caret, and
    // every later paste, nested inside the previous paste's formatting.
    const fragment = document.createDocumentFragment();
    fragment.append(...Array.from(tempDiv.childNodes));
    const lastInserted = fragment.lastChild;

    range.deleteContents();
    range.insertNode(fragment);

    // Blocks pasted inside a styled line must not stay in its <p>
    const startNode = range.startContainer;
    const editableEl = (startNode.nodeType === Node.ELEMENT_NODE
      ? startNode as Element
      : startNode.parentElement)?.closest<HTMLElement>('.note-editable');
    if (editableEl)
    {
      hoistBlocksOutOfParagraphs(editableEl);
    }

    if (lastInserted && lastInserted.isConnected)
    {
      if (isBlockElement(lastInserted))
      {
        range.selectNodeContents(lastInserted);
        range.collapse(false);
      }
      else
      {
        range.setStartAfter(lastInserted);
        range.collapse(true);
      }
    }
    else
    {
      range.collapse(false);
    }

    selection.removeAllRanges();
    selection.addRange(range);
  }
};

// True when the clipboard HTML contains only image(s), no text
export const isImageOnlyHtml = (html: string): boolean =>
{
  const container = document.createElement('div');
  container.innerHTML = CleanPastedHTML(html);

  const images = container.querySelectorAll('img');
  if (images.length === 0) return false;

  images.forEach(img => img.remove());
  return container.textContent?.trim().length === 0;
};

// Extracts and sanitizes the HTML clipboard payload; null if left to the default paste
export const extractValidatedPasteHtml = (e: any): string | null =>
{
  const clipboardData = e.clipboardData || e.originalEvent?.clipboardData || window.clipboardData;
  const clipboardTextHtml = clipboardData?.getData('text/html') ?? '';

  const index = clipboardData?.types?.indexOf('Files') ?? -1;
  if (index !== -1 && !clipboardData?.files[0])
  {
    return null;
  }

  if (!clipboardData || clipboardData.getData('text').includes('FileImage'))
  {
    return null;
  }

  if (clipboardTextHtml.length === 0)
  {
    return null;
  }

  // Pure image paste: Summernote uploads the clipboard file itself, so inserting
  // the HTML payload too would duplicate the image
  const hasImageFile = Array.from<File>(clipboardData?.files ?? []).some(file =>
    file.type.startsWith('image/'),
  );

  if (hasImageFile && isImageOnlyHtml(clipboardTextHtml))
  {
    return null;
  }

  const textPasted = clipboardData.getData('Text/html');
  const $html = $('<body />', { html: textPasted });
  const bodyContent = $($html[0]!.innerHTML);

  // Keep `class` here - Excel Online expresses cell styles via class-based
  // CSS rules (a <style> block + class="xl65" on <td>) rather than inline
  // styles, and changeStyles() needs the classes intact to match those rules
  // against elements before it strips them from the final output.
  bodyContent.find('*').removeAttr('onclick');
  validateUrls(bodyContent);

  // Serialize bodyContent back to HTML after validateUrls mutations
  const tempWrapper = document.createElement('div');
  bodyContent.each((_: number, el: HTMLElement) => { tempWrapper.appendChild(el.cloneNode(true)); });
  return tempWrapper.innerHTML || clipboardTextHtml;
};

// Standard Summernote onPaste handler; getEditableEl returns the .note-editable DOM node
export const onSummernotePaste = (e: any, getEditableEl: () => HTMLElement | null | undefined): void =>
{
  const validatedHtml = extractValidatedPasteHtml(e);
  if (validatedHtml === null) return;

  e.stopPropagation();
  e.preventDefault();

  const editableEl = getEditableEl();
  if (editableEl)
  {
    ensureEditableSelection(editableEl);
  }

  changeStyles(validatedHtml);
};
